import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, ChevronDown, ChevronUp, FileText, FolderOpen, Loader2, Maximize2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buildSongPayload, parseChordPro } from '@/chordpro/index';
import useIsMobile from '@/hooks/useIsMobile';
import useVisualViewport from '@/hooks/useVisualViewport';
import ChordProEditor from './ChordProEditor';
import ChordProRenderer from './ChordProRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ['Bestand', 'Analyse', 'Conversie', 'Controle', 'Opslaan'];
const ACCEPTED = '.pdf,.docx,.txt,.pro,.cho,.chordpro,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const StepIndicator = ({ step }) => <ol className="grid grid-cols-5 gap-1" aria-label="Importstappen">
  {STEPS.map((label, index) => <li key={label} className="min-w-0 text-center"><div className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${step > index + 1 ? 'bg-teal-600 text-white' : step === index + 1 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>{step > index + 1 ? <Check className="h-4 w-4" /> : index + 1}</div><span className="mt-1 block truncate text-[11px] text-slate-500">{label}</span></li>)}
  </ol>;

// Regelnummers komen overeen met de nummers in de bevindingen (regel 1 = eerste regel van de oorspronkelijke tekst).
const numberLines = (text) => (text ? text.split('\n').map((line, index) => `${String(index + 1).padStart(3, ' ')}  ${line}`).join('\n') : '');

const SEVERITY_STYLES = {
  error: 'bg-red-50 text-red-900',
  warning: 'bg-amber-50 text-amber-900',
  info: 'bg-slate-50 text-slate-700',
};
const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
// De backend meldt een geslaagde tekstherkenning in dezelfde lijst als waarschuwingen; dat is er één niet.
const SUCCESS_MESSAGE = 'Tekst succesvol herkend';

// Bevindingen uit de conversie en de brontrouwheidscontrole: soort, ernst en regelnummer in de oorspronkelijke tekst.
const IssueList = ({ issues, label = 'Bevindingen bij de conversie', onJump }) => {
  if (!issues?.length) return null;
  const sorted = [...issues].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 1) - (SEVERITY_ORDER[b.severity] ?? 1));
  return <ul className="space-y-2" aria-label={label}>{sorted.map((issue, index) => <li key={`${issue.kind}-${issue.line}-${index}`} data-severity={issue.severity} className={`flex items-start gap-2 rounded-md p-3 text-sm ${SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.warning}`}>
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
    <span className="min-w-0 flex-1">{issue.line && !issue.message.startsWith('Regel') ? `Regel ${issue.line}: ` : ''}{issue.message}</span>
    {onJump && issue.line ? <button type="button" className="shrink-0 rounded border border-current px-2 py-0.5 text-xs font-medium hover:bg-white/60" onClick={() => onJump(issue.line)}>Toon in bron</button> : null}
  </li>)}</ul>;
};

// Alles wat de analyse meldt: gestructureerde bevindingen, plus overige waarschuwingen die er nog niet in staan.
const AnalysisFindings = ({ analysis }) => {
  const issues = analysis.import_issues || [];
  const known = new Set(issues.map((issue) => issue.message));
  const extra = (analysis.import_warnings || []).filter((warning) => !known.has(warning) && warning !== SUCCESS_MESSAGE);
  return <div className="space-y-2"><IssueList issues={issues} /><WarningList warnings={extra} /></div>;
};

const WarningList = ({ warnings }) => warnings?.length ? <div className="space-y-2">{warnings.map((warning) => <div key={warning} className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{warning}</div>)}</div> : null;

const VIEWS = [['pdf-editor', 'PDF + Editor'], ['editor-preview', 'Editor + Voorbeeld'], ['editor', 'Alleen Editor']];
// Kleine schermen: één werkvlak tegelijk. Dezelfde secties en dezelfde editor-instantie, alleen de zichtbaarheid wisselt.
const MOBILE_VIEWS = [['source', 'Bron'], ['editor', 'Editor'], ['preview', 'Voorbeeld']];
const SPLIT_MIN = 25;
const SPLIT_MAX = 75;

// De oorspronkelijk herkende tekst, met dezelfde regelnummers als de bevindingen; een regel kan worden gemarkeerd.
const SourceText = ({ text, highlight }) => {
  const lines = (text || '').split('\n');
  if (!text) return <p className="p-4 text-sm text-slate-500">Geen tekst beschikbaar</p>;
  return <div className="p-3 font-mono text-xs leading-5" aria-label="Brontekst met regelnummers">{lines.map((line, index) => <div key={index} id={`source-line-${index + 1}`} className={`flex gap-3 whitespace-pre-wrap break-words ${highlight === index + 1 ? 'rounded bg-amber-100' : ''}`}><span className="w-8 shrink-0 select-none text-right text-slate-400">{index + 1}</span><span className="min-w-0 flex-1">{line}</span></div>)}</div>;
};

// Schermvullende correctiewerkruimte: bron links, bewerkbare ChordPro rechts, preview alleen op verzoek. De editor blijft in elke
// weergave dezelfde ene instantie (alleen zijn plek in het raster verandert), zodat tekst, cursor en scrollpositie bewaard blijven.
const ImportWorkspace = ({ analysis, draft, onDraftChange, file, previewUrl, liveIssues, busy, pendingNode, onPrevious, onRecheck, onSave, saveDisabled, onEnlarge, mobile = false, keyboardOpen = false }) => {
  const [view, setView] = useState('pdf-editor');
  const [mobileView, setMobileViewState] = useState('editor');
  const setMobileView = (next) => {
    // Een verborgen veld mag geen focus (en dus geen schermtoetsenbord) houden.

    if (next !== 'editor' && document.activeElement instanceof HTMLElement && document.activeElement.closest('[aria-label="Bewerkbare ChordPro"]')) document.activeElement.blur();
    setMobileViewState(next);
  };
  const [split, setSplit] = useState(50);
  const [dragging, setDragging] = useState(false);
  const [sourceTab, setSourceTab] = useState(previewUrl ? 'pdf' : 'text');
  const [findingsOpen, setFindingsOpen] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const gridRef = useRef(null);

  const issues = liveIssues ?? analysis.import_issues ?? [];
  const known = new Set(issues.map((issue) => issue.message));
  const extra = (analysis.import_warnings || []).filter((warning) => !known.has(warning) && warning !== SUCCESS_MESSAGE);
  const count = (severity) => issues.filter((issue) => issue.severity === severity).length;
  const errors = count('error');
  const warnings = count('warning') + extra.length;
  const infos = count('info');
  const total = issues.length + extra.length;
  const summary = total
    ? `${total} ${total === 1 ? 'bevinding' : 'bevindingen'} — ${errors} ${errors === 1 ? 'fout' : 'fouten'}, ${warnings} ${warnings === 1 ? 'waarschuwing' : 'waarschuwingen'}${infos ? `, ${infos} info` : ''}`
    : 'Geen bevindingen';

  // Na een nieuwe controle of bij fouten staan de details open: fouten worden nooit weggestopt.
  useEffect(() => { if (liveIssues || errors > 0) setFindingsOpen(true); }, [liveIssues, errors]);

  const jumpToSource = (line) => {
    setSourceTab('text');
    if (view !== 'pdf-editor') setView('pdf-editor');
    if (mobile) setMobileView('source');
    setHighlight(line);
    setTimeout(() => document.getElementById(`source-line-${line}`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 0);
  };

  const dragTo = (clientX) => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return;
    setSplit(Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, ((clientX - rect.left) / rect.width) * 100)));
  };

  const split2 = !mobile && view !== 'editor';
  const columns = split2 ? `${split}% 10px minmax(0, 1fr)` : 'minmax(0, 1fr)';
  // Mobiel liggen Bron en Editor in dezelfde rastercel en blijven beide gemount: het niet-actieve vlak is onzichtbaar, inert en aria-hidden,
  // zodat tekst, cursor, selectie en scrollpositie van de editor bewaard blijven. Het Voorbeeld is afgeleid van het concept en wordt alleen
  // gemount zolang het actief is (zijn scrollpositie wordt dus niet bewaard).
  const cell = { gridColumn: 1, gridRow: 1 };
  const place = mobile ? {
    pdf: mobileView === 'source' ? cell : null,
    editor: cell,
    preview: mobileView === 'preview' ? cell : null,
  } : {
    pdf: view === 'pdf-editor' ? { gridColumn: 1 } : null,
    editor: view === 'pdf-editor' ? { gridColumn: 3 } : { gridColumn: 1 },
    preview: view === 'editor-preview' ? { gridColumn: 3 } : null,
  };
  const editorHidden = mobile && mobileView !== 'editor';
  const frozen = dragging ? 'pointer-events-none' : '';

  const findingsButton = <Button type="button" variant="outline" size="sm" className={`${mobile ? 'h-9 w-full justify-between' : 'ml-auto'} ${errors ? 'border-red-300 text-red-800' : ''}`} aria-expanded={findingsOpen} onClick={() => setFindingsOpen((open) => !open)}>
    <span className="flex min-w-0 items-center"><AlertTriangle className="mr-1.5 h-3.5 w-3.5 shrink-0" /><span className={mobile ? 'truncate' : ''}>{summary}</span></span>{findingsOpen ? <ChevronUp className="ml-1.5 h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="ml-1.5 h-3.5 w-3.5 shrink-0" />}
  </Button>;

  return <div className="flex h-full min-h-0 flex-col" data-mobile={mobile ? 'true' : undefined} data-keyboard-open={mobile && keyboardOpen ? 'true' : undefined}>
    <div className={`relative shrink-0 border-b bg-white ${mobile ? 'px-3 py-2 pr-14' : 'px-4 py-2 pr-14'}`}>
      {mobile ? <div className="import-workspace-header space-y-2">
        <DialogHeader className={keyboardOpen ? 'sr-only' : 'min-w-0'}><DialogTitle className="truncate text-base">Controleren en corrigeren</DialogTitle>{!keyboardOpen && <p className="import-workspace-subtitle truncate text-xs text-slate-500">Stap 4 van 5 · {analysis.metadata?.title || file?.name || 'Lied'}</p>}</DialogHeader>
        <div className="grid grid-cols-3 gap-0.5 rounded-md border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label="Werkvlak">
          {MOBILE_VIEWS.map(([value, label]) => <Button key={value} type="button" size="sm" className="h-10 px-1" variant={mobileView === value ? 'default' : 'ghost'} aria-pressed={mobileView === value} onClick={() => setMobileView(value)}>{label}</Button>)}
        </div>
        {!keyboardOpen && <div className="import-workspace-findings">{findingsButton}</div>}
      </div> : <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <DialogHeader className="min-w-0"><DialogTitle className="truncate text-base">Controleren en corrigeren</DialogTitle><p className="text-xs text-slate-500">Stap 4 van 5 · {analysis.metadata?.title || file?.name || 'Lied'}</p></DialogHeader>
        <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label="Weergave">
          {VIEWS.map(([value, label]) => <Button key={value} type="button" size="sm" variant={view === value ? 'default' : 'ghost'} aria-pressed={view === value} onClick={() => setView(value)}>{label}</Button>)}
        </div>
        {findingsButton}
      </div>}
      {findingsOpen && total > 0 && <div className="absolute left-0 right-0 top-full z-20 max-h-[45vh] space-y-2 overflow-auto overscroll-contain border-b bg-white p-4 shadow-lg">
        <IssueList issues={issues} label="Bevindingen bij de huidige ChordPro" onJump={jumpToSource} />
        <WarningList warnings={extra} />
      </div>}
    </div>

    <div ref={gridRef} className="relative grid min-h-0 flex-1" style={{ gridTemplateColumns: columns, ...(mobile ? { gridTemplateRows: 'minmax(0, 1fr)' } : null) }}>
      <section aria-label="Originele bron" className={`flex min-h-0 min-w-0 flex-col border-r bg-slate-50 ${frozen} ${place.pdf ? '' : mobile ? 'pointer-events-none invisible' : 'pointer-events-none invisible absolute left-0 top-0 h-full w-1/2'}`} style={place.pdf || (mobile ? cell : undefined)} aria-hidden={place.pdf ? undefined : true} inert={place.pdf ? undefined : true}>
        <div className="flex shrink-0 items-center justify-between gap-2 border-b bg-white px-3 py-1.5">
          <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label="Bronweergave">
            {previewUrl && <Button type="button" size="sm" variant={sourceTab === 'pdf' ? 'default' : 'ghost'} aria-pressed={sourceTab === 'pdf'} onClick={() => setSourceTab('pdf')}>PDF</Button>}
            <Button type="button" size="sm" variant={sourceTab === 'text' ? 'default' : 'ghost'} aria-pressed={sourceTab === 'text'} onClick={() => setSourceTab('text')}>Brontekst</Button>
          </div>
          {previewUrl && <div className="flex items-center gap-2">
            {mobile && <a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-md border border-slate-200 px-3 text-sm font-medium text-slate-700">PDF openen</a>}
            <Button type="button" variant="outline" size="sm" onClick={onEnlarge}><Maximize2 className="mr-1.5 h-3.5 w-3.5" />Vergroten</Button>
          </div>}
        </div>
        <div className="relative min-h-0 flex-1">
          {previewUrl && <object data={`${previewUrl}#pagemode=none&view=FitH&zoom=page-width`} type="application/pdf" className={`absolute inset-0 h-full w-full ${sourceTab === 'pdf' ? '' : 'invisible'}`} aria-label="PDF-preview"><p className="p-4 text-sm text-slate-600">PDF-preview niet beschikbaar in deze browser. Gebruik “PDF openen” of kijk bij Brontekst.</p></object>}
          {(sourceTab === 'text' || !previewUrl) && <div className="absolute inset-0 overflow-auto bg-white"><SourceText text={analysis.original_extracted_text} highlight={highlight} /></div>}
        </div>
      </section>

      {split2 && <div role="separator" aria-orientation="vertical" aria-label="Verdeling aanpassen" aria-valuemin={SPLIT_MIN} aria-valuemax={SPLIT_MAX} aria-valuenow={Math.round(split)} tabIndex={0}
        className="z-10 cursor-col-resize bg-slate-200 transition-colors hover:bg-teal-400 focus:bg-teal-500 focus:outline-none"
        style={{ gridColumn: 2 }}
        onPointerDown={(event) => { event.currentTarget.setPointerCapture?.(event.pointerId); setDragging(true); }}
        onPointerMove={(event) => { if (dragging) dragTo(event.clientX); }}
        onPointerUp={(event) => { event.currentTarget.releasePointerCapture?.(event.pointerId); setDragging(false); }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') { event.preventDefault(); setSplit((value) => Math.max(SPLIT_MIN, value - 3)); }
          if (event.key === 'ArrowRight') { event.preventDefault(); setSplit((value) => Math.min(SPLIT_MAX, value + 3)); }
        }} />}

      <section aria-label="Bewerkbare ChordPro" className={`min-h-0 min-w-0 bg-white ${frozen} ${editorHidden ? 'pointer-events-none invisible' : ''}`} style={place.editor} aria-hidden={editorHidden ? true : undefined} inert={editorHidden ? true : undefined}>
        <ChordProEditor value={draft} onChange={onDraftChange} initialKey={analysis.metadata?.key} showPreview={false} fill />
      </section>

      {place.preview && <section aria-label="Voorbeeld" className={`import-workspace-preview min-h-0 min-w-0 border-l bg-white ${frozen}`} style={place.preview}>
        <ChordProRenderer source={draft} initialKey={analysis.metadata?.key} />
      </section>}
    </div>

    {pendingNode}

    <div className={mobile ? 'import-workspace-footer flex shrink-0 items-center gap-2 border-t bg-white px-3 py-2' : 'flex shrink-0 flex-wrap items-center justify-between gap-2 border-t bg-white px-4 py-2'}>
      <Button type="button" variant="outline" onClick={onPrevious} disabled={busy} className={mobile ? 'h-11 px-3' : undefined}>Vorige</Button>
      <div className={mobile ? 'flex min-w-0 flex-1 items-center gap-2' : 'flex flex-wrap items-center gap-2'}>
        <Button type="button" variant="outline" onClick={onRecheck} disabled={busy} aria-label="Opnieuw controleren" className={mobile ? 'h-11 min-w-0 flex-1 px-2' : undefined}>{mobile ? 'Controleer' : 'Opnieuw controleren'}</Button>
        <Button type="button" onClick={onSave} disabled={saveDisabled} aria-label="Opslaan als ChordPro" className={`bg-teal-600 text-white hover:bg-teal-700 ${mobile ? 'h-11 min-w-0 flex-[1.4] px-2' : ''}`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{mobile ? 'Opslaan' : 'Opslaan als ChordPro'}</Button>
      </div>
    </div>
  </div>;
};

const WorshipImportWizard = ({ open, onOpenChange, onSaved }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [draft, setDraft] = useState('');
  const [driveFileId, setDriveFileId] = useState('');
  const [busy, setBusy] = useState(false);
  const [pdfEnlarged, setPdfEnlarged] = useState(false);
  // Controle van de huidige (bewerkte) ChordPro tegen de oorspronkelijke tekst.
  const [liveIssues, setLiveIssues] = useState(null);
  // Bevindingen die bevestiging vragen voordat er wordt opgeslagen.
  const [pendingIssues, setPendingIssues] = useState(null);
  const confirmRef = useRef(null);
  const isMobile = useIsMobile();
  const mobileWorkspace = isMobile && step === 4 && !!analysis;
  const viewport = useVisualViewport(open && mobileWorkspace);
  const parsedDraft = useMemo(() => parseChordPro(draft), [draft]);
  const previewUrl = useMemo(() => file?.type === 'application/pdf' ? URL.createObjectURL(file) : null, [file]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  // De bevestiging staat onderaan een lange dialoog: breng haar in beeld en geef haar de focus, zodat ze niet over het hoofd wordt gezien.
  useEffect(() => {
    if (pendingIssues && confirmRef.current) {
      confirmRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      confirmRef.current.focus?.({ preventScroll: true });
    }
  }, [pendingIssues]);
  useEffect(() => {
    if (!open) {
      setStep(1); setFile(null); setAnalysis(null); setDraft(''); setDriveFileId(''); setBusy(false); setPdfEnlarged(false); setLiveIssues(null); setPendingIssues(null);
    }
  }, [open]);

  const analyze = async () => {
    if (!file) { toast.error('Kies eerst een liedbestand'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (driveFileId) form.append('google_drive_file_id', driveFileId);
      const response = await axios.post(`${API}/worship/songs/import/analyze`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAnalysis(response.data);
      setDraft(response.data.chordpro);
      setLiveIssues(null);
      setPendingIssues(null);
      setStep(2);
      toast.success(response.data.scanned_pdf ? 'PDF-analyse afgerond; OCR is nodig' : 'Tekst succesvol herkend');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bestand analyseren mislukt');
    } finally {
      setBusy(false);
    }
  };

  // Vergelijkt de huidige ChordPro met de oorspronkelijke tekst (server-side, stateless).
  const verifyDraft = async () => {
    const response = await axios.post(`${API}/worship/songs/import/verify`, {
      original_text: analysis.original_extracted_text || '', chordpro: draft,
    });
    setLiveIssues(response.data.import_issues);
    return response.data;
  };

  const recheck = async () => {
    setBusy(true);
    try {
      const result = await verifyDraft();
      toast[result.verification.has_errors ? 'error' : 'success'](result.verification.has_errors ? 'Er ontbreekt nog tekst; zie de bevindingen' : 'Geen afwijkingen van de oorspronkelijke tekst gevonden');
    } catch (error) {
      toast.error('Controle uitvoeren mislukt');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!parsedDraft.validation.valid) { toast.error(parsedDraft.validation.errors[0]); return; }
    // Aantoonbaar tekstverlies mag niet ongemerkt worden opgeslagen: eerst controleren en laten bevestigen.
    setBusy(true);
    try {
      let result;
      try {
        result = await verifyDraft();
      } catch (error) {
        setPendingIssues([{ kind: 'controle', severity: 'warning', line: null, message: 'De brontrouwheidscontrole kon niet worden uitgevoerd; de conversie is niet vergeleken met de oorspronkelijke tekst.' }]);
        return;
      }
      if (result.verification.has_errors) {
        setPendingIssues(result.import_issues.filter((issue) => issue.severity === 'error'));
        return;
      }
    } finally {
      setBusy(false);
    }
    await persist();
  };

  const persist = async () => {
    setPendingIssues(null);
    setBusy(true);
    try {
      const payload = buildSongPayload(draft, {
        publisher: analysis.metadata?.publisher,
        source: analysis.metadata?.source,
        google_drive_file_id: driveFileId || undefined,
      }, analysis);
      const response = await axios.post(`${API}/worship/songs`, payload);
      setStep(5);
      toast.success('ChordPro succesvol opgeslagen');
      onSaved?.(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lied opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  const pendingNode = pendingIssues ? <div ref={confirmRef} tabIndex={-1} role="alert" className="shrink-0 space-y-3 border-t border-red-200 bg-red-50 p-4 text-sm text-red-900">
        <p className="font-semibold">Controleer dit voordat je opslaat</p>
        <p>De ChordPro bevat niet alle tekst uit het oorspronkelijke bestand. Als je nu opslaat gaat de ontbrekende tekst verloren in het lied.</p>
        <IssueList issues={pendingIssues} label="Ontbrekende tekst" />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => setPendingIssues(null)}>Terug om te corrigeren</Button>
          <Button type="button" onClick={persist} disabled={busy} className="bg-red-700 text-white hover:bg-red-800">Toch opslaan</Button>
        </div>
      </div> : null;

  return <>
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={step === 4 && analysis ? 'import-workspace-dialog flex h-[96vh] w-[98vw] max-w-[98vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[98vw] sm:rounded-lg' : 'max-h-[96vh] overflow-y-auto sm:max-w-[96vw] lg:max-w-6xl'}
      style={mobileWorkspace && viewport.height && !viewport.zoomed ? { '--import-vvh': `${viewport.height - 16}px`, '--import-vvtop': `${viewport.offsetTop}px` } : undefined}>
      {step === 4 && analysis ? <ImportWorkspace analysis={analysis} draft={draft} onDraftChange={setDraft} file={file} previewUrl={previewUrl} liveIssues={liveIssues} busy={busy}
        pendingNode={pendingNode} onPrevious={() => setStep(3)} onRecheck={recheck} onSave={save} saveDisabled={busy || !parsedDraft.validation.valid} onEnlarge={() => setPdfEnlarged(true)} mobile={isMobile} keyboardOpen={viewport.keyboardOpen} /> : <>
      <DialogHeader><DialogTitle>Liedbestanden importeren naar ChordPro</DialogTitle></DialogHeader>
      <StepIndicator step={step} />

      {step === 1 && <div className="grid gap-5 py-4 md:grid-cols-2">
        <label className="flex min-h-56 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center hover:border-teal-500 hover:bg-teal-50/40">
          <Upload className="mb-3 h-9 w-9 text-teal-700" /><span className="font-semibold text-slate-900">Kies een bestaand liedbestand</span><span className="mt-1 text-sm text-slate-500">PDF, DOCX, TXT, PRO, CHO of CHORDPRO · maximaal 15 MB</span>
          {file && <span className="mt-4 rounded bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm">{file.name}</span>}
          <input className="sr-only" type="file" accept={ACCEPTED} onChange={(event) => setFile(event.target.files?.[0] || null)} />
        </label>
        <div className="flex min-h-56 flex-col justify-center rounded-md border border-slate-200 p-6"><FolderOpen className="mb-3 h-8 w-8 text-slate-400" /><h3 className="font-semibold text-slate-900">Google Drive</h3><p className="mt-1 text-sm text-slate-500">Een Drive-bestandskiezer wordt beschikbaar zodra Google OAuth voor deze organisatie is gekoppeld. Zoea slaat geen Google-wachtwoorden op.</p><div className="mt-4"><Label>Optioneel bestaand Drive FileId</Label><Input value={driveFileId} onChange={(event) => setDriveFileId(event.target.value)} placeholder="Alleen als het lokale bestand uit Drive komt" /></div></div>
        <p className="text-xs text-slate-500 md:col-span-2">Let op: de organisatie blijft zelf verantwoordelijk voor auteursrechten, CCLI en overige licenties van geïmporteerde liederen.</p>
      </div>}

      {step === 2 && analysis && <div className="space-y-4 py-4"><div className="grid gap-3 sm:grid-cols-4"><div className="rounded-md bg-slate-50 p-4"><p className="text-xs text-slate-500">Bron</p><p className="mt-1 font-semibold uppercase">{analysis.source_type}</p></div><div className="rounded-md bg-slate-50 p-4"><p className="text-xs text-slate-500">Titel</p><p className="mt-1 font-semibold">{analysis.metadata?.title || 'Niet herkend'}</p></div><div className="rounded-md bg-slate-50 p-4"><p className="text-xs text-slate-500">Status</p><p className="mt-1 font-semibold">{analysis.scanned_pdf ? 'OCR nodig' : 'Tekst herkend'}</p></div><div className="rounded-md bg-slate-50 p-4"><p className="text-xs text-slate-500">Zekerheid</p><p className="mt-1 font-semibold">{Math.round(analysis.confidence_score * 100)}%</p></div></div><AnalysisFindings analysis={analysis} /><div><Label>Geëxtraheerde tekst met regelnummers</Label><pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-white p-4 text-sm">{numberLines(analysis.original_extracted_text) || 'Geen tekstlaag gevonden. Vul het lied in stap 4 handmatig aan of gebruik later OCR.'}</pre></div></div>}

      {step === 3 && analysis && <div className="grid gap-4 py-4 lg:grid-cols-[320px_1fr]"><div className="space-y-3"><div className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800"><Check className="mb-2 h-5 w-5" />ChordPro succesvol gegenereerd</div><AnalysisFindings analysis={analysis} /><p className="text-xs text-slate-500">Bij twijfel blijft de oorspronkelijke tekst behouden. Bevindingen met een regelnummer verwijzen naar de oorspronkelijke tekst; je kunt alles in de volgende stap corrigeren.</p></div><div className="h-[520px] overflow-hidden rounded-md border"><ChordProRenderer source={draft} /></div></div>}

      {step === 5 && <div className="py-16 text-center"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-7 w-7" /></div><h3 className="mt-4 text-xl font-semibold">Lied opgeslagen</h3><p className="mt-2 text-slate-500">Het ChordPro-lied is direct beschikbaar in Liederen en Setlists.</p></div>}

      <DialogFooter className="gap-2">
        {step > 1 && step < 4 && <Button type="button" variant="outline" onClick={() => setStep((value) => value - 1)} disabled={busy}>Vorige</Button>}
        {step === 1 && <Button type="button" onClick={analyze} disabled={!file || busy} className="bg-teal-600 text-white hover:bg-teal-700">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Analyseren</Button>}
        {step === 2 && <Button type="button" onClick={() => setStep(3)} className="bg-teal-600 text-white hover:bg-teal-700">Conversie bekijken</Button>}
        {step === 3 && <Button type="button" onClick={() => setStep(4)} className="bg-teal-600 text-white hover:bg-teal-700">Controleren en corrigeren</Button>}
        {step === 5 && <Button type="button" onClick={() => onOpenChange(false)} className="bg-teal-600 text-white hover:bg-teal-700">Sluiten</Button>}
      </DialogFooter>
      </>}
    </DialogContent>
  </Dialog>
  <Dialog open={pdfEnlarged} onOpenChange={setPdfEnlarged}>
    <DialogContent className="max-h-[96vh] w-full max-w-[96vw] overflow-hidden">
      <DialogHeader><DialogTitle>Originele bron{file ? ` — ${file.name}` : ''}</DialogTitle></DialogHeader>
      {previewUrl && <object data={previewUrl} type="application/pdf" className="h-[85vh] w-full rounded-md border" aria-label="PDF-preview vergroot"><p>PDF-preview niet beschikbaar.</p></object>}
    </DialogContent>
  </Dialog>
  </>;
};

export default WorshipImportWizard;
